import { useState } from 'react';
import { PlusIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import {
  WORKBOOK_MAX_COLUMNS,
  workbookColumnarSheetSchema,
  workbookColumnSchema,
  type WorkbookColumnarSheet,
} from '@erp/shared-types';
import type { ColumnType } from '@erp/workbooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { WorksheetFormulaInput } from './WorksheetFormulaInput';
import { useColumnTypeOptions } from './worksheetTypeLabels';
import { WorksheetLookups } from './WorksheetLookups';
import type { ChangeSheet } from './useWorkbookEditor';
import { lookupDraft, mergeLookupDraft } from './worksheetLookupDraft';
import { nextColumnName } from './worksheetColumns';
import { planAddedColumns } from './worksheetAddColumns';
import type { FormulaFields } from './worksheetFormulaFields';

export function WorksheetAddColumn({
  sheet,
  inputColumns,
  formulaFields,
  defaultTypes,
  lookupType,
  items,
  selected,
  setSelected,
  readOnly,
  change,
  onAdded,
}: {
  sheet: WorkbookColumnarSheet;
  inputColumns: string[];
  /** The Fields a new column's Formula may reference. */
  formulaFields: FormulaFields;
  /** Suggested types for columns made from Fields; they only seed the default. */
  defaultTypes: ReadonlyMap<string, ColumnType>;
  /** The type a new Lookup result column takes. */
  lookupType: (lookup: WorkbookColumnarSheet['lookups'][number]) => ColumnType;
  items: { id: string; name: string }[];
  selected: string[];
  setSelected: (columns: string[]) => void;
  readOnly: boolean;
  change: ChangeSheet;
  onAdded: (index: number) => void;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'columns' | 'lookup'>('columns');
  const [formula, setFormula] = useState('');
  const [customName, setName] = useState<string | null>(null);
  const [customType, setType] = useState<
    WorkbookColumnarSheet['columns'][number]['type'] | null
  >(null);
  // The lookup draft is merged onto the latest sheet on apply, so lookup
  // changes made while the popover is open are not overwritten.
  const [lookupSeed, setLookupSeed] = useState(sheet.lookups);
  const [draft, setDraft] = useState(() => lookupDraft(sheet.lookups));
  const lookups = draft.map((entry) => entry.lookup);
  const [error, setError] = useState('');
  const names = new Set(
    sheet.columns.map((column) => column.name.toLowerCase()),
  );
  const available = Array.from(
    new Set([
      ...inputColumns,
      ...sheet.lookups.map((lookup) => lookup.outputColumn),
    ]),
  );
  const additions = selected.filter(
    (column) => available.includes(column) && !names.has(column.toLowerCase()),
  );
  const single = additions.length === 1 ? additions[0] : undefined;
  const name =
    customName ??
    single ??
    nextColumnName(
      gt('New column', { context: 'worksheet default column name' }),
      sheet.columns,
    );
  const type =
    customType ?? (single ? defaultTypes.get(single) : undefined) ?? 'text';
  const types = useColumnTypeOptions();
  function addColumns() {
    const plan = planAddedColumns({
      existing: sheet.columns,
      additions,
      name,
      type,
      formula,
      defaultTypes,
    });
    if (plan.kind === 'none') return;
    if (plan.kind === 'invalid') {
      setError(
        gt('Use a unique column name and a non-empty formula.', {
          context: 'worksheet add formula validation',
        }),
      );
      return;
    }
    const { columns } = plan;
    change((latest) => ({
      ...latest,
      columns: [...latest.columns, ...columns],
    }));
    onAdded(sheet.columns.length);
    setSelected([]);
    setOpen(false);
  }
  function applyLookups() {
    const parsed = workbookColumnarSheetSchema.shape.lookups.safeParse(lookups);
    const lookupNames = lookups.map((lookup) =>
      lookup.outputColumn.toLowerCase(),
    );
    if (
      !parsed.success ||
      new Set(lookupNames).size !== lookupNames.length ||
      lookupNames.some((name) =>
        inputColumns.some((column) => column.toLowerCase() === name),
      )
    ) {
      setError(
        gt(
          "Complete each lookup and give its result a name that isn't already a column name.",
          { context: 'worksheet lookup validation' },
        ),
      );
      return;
    }
    const merged = mergeLookupDraft(sheet, lookupSeed, draft, lookupType);
    if (merged.columns.length > WORKBOOK_MAX_COLUMNS) {
      setError(
        gt('A worksheet can contain up to 100 columns.', {
          context: 'worksheet column limit',
        }),
      );
      return;
    }
    change((latest) => mergeLookupDraft(latest, lookupSeed, draft, lookupType));
    if (merged.columns.length > sheet.columns.length)
      onAdded(sheet.columns.length);
    setOpen(false);
  }
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setMode('columns');
          setFormula('');
          setName(null);
          setType(null);
          setLookupSeed(sheet.lookups);
          setDraft(lookupDraft(sheet.lookups));
          setError('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" disabled={readOnly}>
          <PlusIcon />
          <T context="worksheet add column action">Add column</T>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[28rem] max-w-[calc(100vw-2rem)] max-h-[70vh] space-y-3 overflow-auto p-3"
        aria-label={gt('Add column', {
          context: 'worksheet add column action',
        })}
      >
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={mode === 'columns' ? 'secondary' : 'ghost'}
            onClick={() => setMode('columns')}
          >
            <T context="worksheet column creation mode">Columns & formula</T>
          </Button>
          <Button
            size="sm"
            variant={mode === 'lookup' ? 'secondary' : 'ghost'}
            onClick={() => setMode('lookup')}
          >
            <T context="worksheet lookup creation mode">Data lookup</T>
          </Button>
        </div>
        <fieldset disabled={readOnly} className="space-y-3">
          {mode === 'columns' ? (
            <>
              <div className="max-h-44 space-y-1 overflow-auto">
                {available.map((column) => (
                  <label
                    key={column}
                    className="flex items-center gap-2 text-sm"
                  >
                    <Checkbox
                      checked={
                        names.has(column.toLowerCase()) ||
                        selected.includes(column)
                      }
                      disabled={names.has(column.toLowerCase())}
                      onCheckedChange={() =>
                        setSelected(
                          selected.includes(column)
                            ? selected.filter((item) => item !== column)
                            : [...selected, column],
                        )
                      }
                    />
                    {column}
                    {names.has(column.toLowerCase()) && (
                      <span className="text-xs text-muted-foreground">
                        <T context="worksheet column already included">
                          Already added
                        </T>
                      </span>
                    )}
                  </label>
                ))}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setSelected(
                    available.filter(
                      (column) => !names.has(column.toLowerCase()),
                    ),
                  )
                }
              >
                <T context="worksheet select missing input columns">
                  Select remaining
                </T>
              </Button>
              <div className="space-y-2 border-t pt-3">
                <p className="text-xs font-medium">
                  {single ? (
                    <T context="worksheet single column settings">
                      Column & optional formula
                    </T>
                  ) : (
                    <T context="worksheet optional formula">
                      Formula column (optional)
                    </T>
                  )}
                </p>
                <div className="flex gap-2">
                  <Input
                    aria-label={gt('New column name', {
                      context: 'worksheet create column name',
                    })}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                  <Select
                    value={type}
                    onValueChange={(value) =>
                      setType(workbookColumnSchema.shape.type.parse(value))
                    }
                  >
                    <SelectTrigger
                      className="min-w-36"
                      aria-label={gt('New column type', {
                        context: 'worksheet create column type',
                      })}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {types.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <WorksheetFormulaInput
                  optional
                  value={formula}
                  fields={formulaFields}
                  readOnly={readOnly}
                  label={gt('New column formula', {
                    context: 'worksheet create column formula',
                  })}
                  onChange={setFormula}
                />
              </div>
              <Button
                size="sm"
                disabled={
                  (!additions.length && !formula.trim()) ||
                  sheet.columns.length +
                    additions.length +
                    Number(
                      Boolean(formula.trim()) &&
                        !(
                          single &&
                          name.trim().toLowerCase() === single.toLowerCase()
                        ),
                    ) >
                    WORKBOOK_MAX_COLUMNS
                }
                onClick={addColumns}
              >
                <T context="worksheet confirm added columns">Add columns</T>
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                <T context="worksheet lookup creation guidance">
                  Match a column against another input or data mapping to return
                  a value. New lookup results become output columns and can be
                  used in formulas.
                </T>
              </p>
              <WorksheetLookups
                lookups={lookups}
                items={items}
                update={(index, patch) =>
                  setDraft((entries) =>
                    entries.map((entry, i) =>
                      i === index
                        ? { ...entry, lookup: { ...entry.lookup, ...patch } }
                        : entry,
                    ),
                  )
                }
                remove={(index) =>
                  setDraft((entries) => entries.filter((_, i) => i !== index))
                }
                add={(lookup) =>
                  setDraft((entries) => [...entries, { lookup }])
                }
              />
              <Button size="sm" onClick={applyLookups}>
                <T context="worksheet apply lookup functions">Apply lookups</T>
              </Button>
            </>
          )}
        </fieldset>
        {error && (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
